import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { openExternalUrl } from "@/lib/utils";
import { logError } from "@/lib/logger";
import { useToast } from "@/hooks/use-toast";
import { EXTENSION_HELP_ALSO, EXTENSION_INSTALL_URL, EXTENSION_DOWNLOAD_CTA } from "@/lib/extensionCopy";
import { useExtensionInstalled } from "@/hooks/useExtensionInstalled";

interface ManualFallbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId?: string | null;
  jobTitle?: string;
  companyName?: string;
  applyUrl?: string | null;
  errorReason?: string | null;
  payloadSnapshot?: Record<string, unknown> | null;
  onMarkAsApplied?: (jobId: string, jobTitle: string, companyName?: string, jobUrl?: string) => Promise<boolean | void>;
}

/**
 * Zero-friction manual fallback dialog
 * Visas när automation misslyckas - användare kan öppna pre-filled form direkt
 * "Don't Make Me Think" - en knapp, allt är redan klart
 */
export function ManualFallbackDialog({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  companyName,
  applyUrl,
  errorReason,
  payloadSnapshot,
  onMarkAsApplied,
}: ManualFallbackDialogProps) {
  const { toast } = useToast();
  const extensionInstalled = useExtensionInstalled();

  const handleOpenForm = () => {
    if (!applyUrl) {
      toast({
        title: "Ingen ansökningslänk",
        description: "Kontakta företaget direkt för att ansöka.",
        variant: "destructive",
      });
      return;
    }

    // Öppna extern länk
    if (openExternalUrl(applyUrl)) {
      toast({
        title: "Öppnar ansökningsformulär...",
        description: "Formuläret är förifyllt med din information. Klicka 'Skicka' när du är klar.",
        duration: 5000,
      });
      onOpenChange(false);
    }
  };

  const hasCaptcha = errorReason?.toLowerCase().includes("captcha") || 
                     errorReason?.toLowerCase().includes("hcaptcha") ||
                     errorReason?.toLowerCase().includes("recaptcha");
  const hasLogin = errorReason?.toLowerCase().includes("login") ||
                   errorReason?.toLowerCase().includes("authentication");

  // Visa vad som är förifyllt
  const prefilledFields = payloadSnapshot ? Object.keys(payloadSnapshot).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-orange-100 dark:bg-orange-900/30 p-3">
              <AlertCircle className="h-6 w-6 text-orange-600 dark:text-orange-400" aria-hidden />
            </div>
            <DialogTitle className="text-xl">Ett sista steg krävs</DialogTitle>
          </div>
          <DialogDescription className="text-base">
            {jobTitle && companyName ? (
              <>
                Vi kunde inte skicka ansökan automatiskt till <strong>{jobTitle}</strong> hos{" "}
                <strong>{companyName}</strong>.
              </>
            ) : (
              "Vi kunde inte skicka ansökan automatiskt."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Vad som är förifyllt */}
          {prefilledFields > 0 && (
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden />
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  Formuläret är förifyllt
                </p>
              </div>
              <p className="text-sm text-green-800 dark:text-green-200">
                {prefilledFields} fält är redan ifyllda med din information. Du behöver bara klicka "Skicka".
              </p>
            </div>
          )}

          {/* Vad användaren behöver göra */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-3">
            <p className="text-sm font-medium">Vad behöver du göra?</p>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              {hasCaptcha && (
                <li>Lös säkerhetskontrollen (CAPTCHA) på sidan</li>
              )}
              {hasLogin && (
                <li>Logga in med ditt konto om det behövs</li>
              )}
              <li>Kontrollera att allt ser rätt ut</li>
              <li>Klicka "Skicka" eller "Ansök" på sidan</li>
            </ol>
          </div>

          {/* Info om vad som händer */}
          <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
            <FileText className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Allt är redan klart</p>
              <p className="text-xs text-muted-foreground mt-1">
                Vi har fyllt i formuläret med din information. Du behöver bara öppna sidan och skicka. {EXTENSION_HELP_ALSO}
                {extensionInstalled !== true && (
                  <>{" "}<a href={EXTENSION_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{EXTENSION_DOWNLOAD_CTA}</a>.</>
                )}
                {extensionInstalled === true && "."}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto min-h-[var(--touch-target)]"
            aria-label="Stäng, inte nu"
          >
            Inte nu
          </Button>
          {onMarkAsApplied && jobId && jobTitle && (
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                try {
                  await onMarkAsApplied(jobId, jobTitle, companyName, applyUrl || undefined);
                  toast({
                    title: "Markerad som ansökt",
                    description: "Jobbet har markerats som ansökt i din lista.",
                  });
                  onOpenChange(false);
                } catch (error) {
                  logError("Error marking as applied", error);
                  toast({
                    title: "Fel",
                    description: "Kunde inte markera jobbet som ansökt.",
                    variant: "destructive",
                  });
                }
              }}
              className="w-full sm:w-auto gap-2 min-h-[var(--touch-target)]"
              aria-label="Markera jobbet som ansökt"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Markera som ansökt
            </Button>
          )}
          <Button
            type="button"
            onClick={handleOpenForm}
            className="w-full sm:w-auto gap-2 min-h-[var(--touch-target)]"
            disabled={!applyUrl}
            aria-label="Öppna ansökningsformulär och skicka"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Öppna formulär och skicka
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
